import React, { useEffect, useState } from 'react'
import {
  collection, doc, updateDoc, deleteDoc, onSnapshot, setDoc, serverTimestamp
} from 'firebase/firestore'
import { initializeApp, getApps } from 'firebase/app'
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth'
import { auth, db } from '../../firebase/firebase'
import { useAuth } from '../../contexts/AuthContext'
import AdminLayout from './AdminLayout'

// Secondary Firebase app — used to create users without signing out the current admin
const secondaryApp = getApps().find(a => a.name === 'secondary')
  || initializeApp({
      apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId:             process.env.REACT_APP_FIREBASE_APP_ID,
    }, 'secondary')
const secondaryAuth = getAuth(secondaryApp)


const emptyForm = { email: '', password: '', displayName: '', roleId: '', permissions: [] }

const AdminUsers = () => {
  const { currentUser } = useAuth()
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'users'), snap =>
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
    const u2 = onSnapshot(collection(db, 'roles'), snap =>
      setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
    return () => { u1(); u2() }
  }, [])

  const handleRoleChange = (roleId) => {
    const role = roles.find(r => r.id === roleId)
    setForm(f => ({ ...f, roleId, permissions: role?.permissions || [] }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editingId) {
        await updateDoc(doc(db, 'users', editingId), {
          displayName: form.displayName.trim(),
          roleId: form.roleId,
          permissions: form.permissions,
        })
      } else {
        const cred = await createUserWithEmailAndPassword(secondaryAuth, form.email, form.password)
        await setDoc(doc(db, 'users', cred.user.uid), {
          uid: cred.user.uid,
          email: form.email,
          displayName: form.displayName.trim(),
          roleId: form.roleId,
          permissions: form.permissions,
          createdAt: serverTimestamp(),
        })
        await secondaryAuth.signOut()
      }
      setForm(emptyForm); setEditingId(null); setShowForm(false)
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (user) => {
    setEditingId(user.id)
    setForm({ email: user.email, password: '', displayName: user.displayName || '', roleId: user.roleId || '', permissions: user.permissions || [] })
    setShowForm(true)
  }

  const handleDelete = async (user) => {
    if (user.uid === currentUser?.uid) return
    if (!window.confirm(
      `Видалити профіль ${user.email} з бази?\n\n` +
      `Увага: акаунт залишиться в Firebase Authentication. ` +
      `Щоб потім додати цей email знову — спочатку видаліть його вручну: ` +
      `Firebase Console → Authentication → знайти email → видалити.`
    )) return
    setDeleting(user.id)
    try {
      await deleteDoc(doc(db, 'users', user.id))
    } catch (err) {
      alert('Помилка видалення: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  return (
    <AdminLayout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Користувачі</h1>
            <p style={s.pageDesc}>Керуйте адміністраторами та їхніми правами доступу</p>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} style={s.btnPrimary}>
              + Новий користувач
            </button>
          )}
        </div>

        {showForm && (
          <div style={{ ...s.card, marginBottom: '24px' }}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>👤</span>
              <span style={s.cardTitle}>{editingId ? 'Редагувати користувача' : 'Новий користувач'}</span>
            </div>
            <form onSubmit={handleSave} style={s.formReset}>
              <div style={s.formGrid}>
                <div style={s.field}>
                  <label style={s.label}>Ім'я</label>
                  <input
                    value={form.displayName}
                    onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                    style={s.input}
                    placeholder="Ім'я та прізвище"
                  />
                </div>
                {!editingId && (
                  <>
                    <div style={s.field}>
                      <label style={s.label}>Email</label>
                      <input
                        type='email'
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        style={s.input}
                        required
                        placeholder='user@example.com'
                      />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Пароль</label>
                      <input
                        type='password'
                        value={form.password}
                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        style={s.input}
                        required
                        placeholder='Мінімум 6 символів'
                        minLength={6}
                      />
                    </div>
                  </>
                )}
                <div style={s.field}>
                  <label style={s.label}>Роль</label>
                  <select
                    value={form.roleId}
                    onChange={e => handleRoleChange(e.target.value)}
                    style={s.input}
                  >
                    <option value=''>— без ролі —</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.btnRow}>
                <button type='submit' disabled={saving} style={s.btnPrimary}>
                  {saving ? 'Збереження…' : editingId ? 'Зберегти зміни' : 'Створити'}
                </button>
                <button type='button' onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(false) }} style={s.btnGhost}>
                  Скасувати
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardIcon}>👥</span>
            <span style={s.cardTitle}>Всі користувачі</span>
            <span style={s.badge}>{users.length}</span>
          </div>

          {users.length === 0 ? (
            <div style={s.empty}>Користувачів ще немає</div>
          ) : (
            <div style={s.userList}>
              {users.map(user => {
                const role = roles.find(r => r.id === user.roleId)
                return (
                  <div key={user.id} style={s.userRow}>
                    <div style={s.userAvatar}>{initials(user.displayName || user.email)}</div>
                    <div style={s.userInfo}>
                      <div style={s.userName}>{user.displayName || <em style={{ color: '#b8a090' }}>Без імені</em>}</div>
                      <div style={s.userEmail}>{user.email}</div>
                    </div>
                    <div style={s.userMeta}>
                      {role
                        ? <span style={s.roleBadge}>{role.name}</span>
                        : <span style={s.noRole}>—</span>}
                    </div>
                    <div style={s.userActions}>
                      <button onClick={() => handleEdit(user)} style={s.actionBtn}>✏️ Редагувати</button>
                      {user.uid !== currentUser?.uid && (
                        <button
                          onClick={() => handleDelete(user)}
                          disabled={deleting === user.id}
                          style={{ ...s.actionBtn, color: '#b84040', borderColor: '#f5d0d0' }}
                        >
                          🗑 Видалити
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

const s = {
  page: { maxWidth: '900px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  pageTitle: { margin: '0 0 6px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc: { margin: 0, fontSize: '14px', color: '#9a7c63' },

  card: {
    background: '#fff',
    borderRadius: '14px',
    padding: '28px',
    boxShadow: '0 2px 16px rgba(92,61,46,0.07)',
    border: '1px solid #f0e4d4',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '22px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f5ede3',
  },
  cardIcon: { fontSize: '18px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', flex: 1 },
  badge: {
    background: '#f0e6d8',
    color: '#8b6044',
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },

  formReset: {
    width: '100%', margin: 0, marginTop: 0, padding: 0,
    border: 'none', borderRadius: 0, background: 'none', boxShadow: 'none', display: 'block',
  },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' },
  field: { marginBottom: '18px' },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: '#8b6044',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#2d1f14',
    background: '#fdfaf7',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
  },

  btnRow: { display: 'flex', gap: '10px', marginTop: '8px' },
  btnPrimary: {
    padding: '11px 24px',
    background: 'linear-gradient(135deg, #6b4a32, #8b6044)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhost: {
    padding: '11px 20px',
    background: 'transparent',
    color: '#8b6044',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  empty: { color: '#b8a090', fontSize: '14px', padding: '16px 0', textAlign: 'center' },

  userList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 16px',
    borderRadius: '10px',
    transition: 'background 0.15s',
    background: '#fdfaf7',
    marginBottom: '6px',
    border: '1px solid #f5ede3',
  },
  userAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #c4986a, #8b6044)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontSize: '14px', fontWeight: '600', color: '#2d1f14', marginBottom: '2px' },
  userEmail: { fontSize: '12px', color: '#9a7c63' },
  userMeta: { flexShrink: 0 },
  roleBadge: {
    background: '#f0e6d8',
    color: '#7a5030',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  noRole: { fontSize: '13px', color: '#c8b0a0' },
  userActions: { display: 'flex', gap: '8px', flexShrink: 0 },
  actionBtn: {
    background: '#fff',
    border: '1px solid #e8d8c8',
    borderRadius: '7px',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '6px 12px',
    color: '#6b4a32',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
}

export default AdminUsers
